/**
 * Recovery for route and modal chunks that fail to load.
 *
 * Two causes, one cure. A deploy rebuilds the client with fresh content hashes
 * and a pod ships only its own `dist`, so a tab left open across one keeps
 * asking for file names that no longer exist. A dropped connection — the
 * ordinary state of a mobile webview — fails a single request while everything
 * else about the build is fine.
 *
 * Both end in a reload, because nothing smaller works: the browser records a
 * module it could not fetch as failed, and every later import of that URL
 * returns the stored error without a network request. A new document is the
 * only way to clear it, and the document is rendered by SSR on every request,
 * so it always arrives with the hashes the server is serving now.
 */

/** Cross-browser wordings for "this dynamic import could not be fetched". */
const STALE_CHUNK_SIGNATURES = [
  // Chrome, Edge
  'Failed to fetch dynamically imported module',
  // Firefox
  'error loading dynamically imported module',
  // Safari
  'Importing a module script failed',
  // Vite's CSS preload helper
  'Unable to preload CSS',
];

/**
 * Whether a chunk failed to load, as opposed to loading and then throwing.
 */
export const isStaleChunkError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return STALE_CHUNK_SIGNATURES.some((signature) =>
    message.includes(signature),
  );
};

let recovering: Promise<Recovery> | null = null;

/**
 * Shape of the value `create-build-hash` writes. Anything else — an HTML error
 * page from a proxy, a maintenance placeholder — would look like "a different
 * build" on every attempt and reload the page forever.
 */
const BUILD_HASH = /^[0-9a-f-]{8,64}$/i;

/**
 * What one probe of the server established.
 *
 * `networkIsUp` is the byproduct that matters as much as the verdict: the probe
 * is the only evidence in the browser that requests are actually being answered
 * right now. `navigator.onLine` is not evidence — it reports whether a network
 * interface exists, and stays `true` through a dead mobile radio, a captive
 * portal and broken DNS.
 */
interface Recovery {
  reloading: boolean;
  networkIsUp: boolean;
}

const startRecovery = async (): Promise<Recovery> => {
  let current: string;
  try {
    const response = await fetch('/buildHash.txt', { cache: 'no-store' });
    if (!response.ok) {
      // The server answered, so the connection works; there is just nothing
      // trustworthy to compare against.
      return { reloading: false, networkIsUp: true };
    }
    current = (await response.text()).trim();
  } catch {
    return { reloading: false, networkIsUp: false };
  }

  const loaded = window.__BUILD_HASH__;
  if (!loaded || !BUILD_HASH.test(current) || current === loaded) {
    return { reloading: false, networkIsUp: true };
  }

  window.location.reload();
  return { reloading: true, networkIsUp: true };
};

/**
 * Reloads the page when — and only when — the server serves a different build
 * than the one this document was rendered from, and reports whether the server
 * answered at all.
 *
 * Comparing build hashes instead of counting attempts is what makes the reload
 * safe to trigger on every failed chunk: afterwards the two hashes agree, so a
 * chunk that still fails cannot trigger a second one. A failure with no deploy
 * behind it falls through to `recoverFromChunkFailure`, which has no such proof
 * and therefore needs both a budget and the `networkIsUp` this returns.
 */
export const recoverFromNewBuild = (): Promise<Recovery> => {
  if (import.meta.env.SSR) {
    return Promise.resolve({ reloading: false, networkIsUp: false });
  }

  recovering ??= startRecovery().then((recovery) => {
    // Only a started reload is final. Leaving anything else cached would blind
    // the next failure to a deploy that happens later in the session — and
    // would report a stale view of the connection along with it.
    if (!recovery.reloading) {
      recovering = null;
    }
    return recovery;
  });

  return recovering;
};

/**
 * Counts the reloads already spent on chunks that failed for no reason a reload
 * can be proven to fix. Survives the reload it guards, which is the whole
 * point, and dies with the tab.
 */
const CHUNK_RELOAD_KEY = 'saas:chunk-reload-count';

/**
 * How many reloads one unresolved incident may spend.
 *
 * Counting attempts rather than measuring elapsed time is what makes this
 * terminate. The obvious guard — "no second reload within N seconds" — assumes
 * a failure arrives promptly, and the failure this exists for does not: a
 * request that hangs on a dead connection rejects on the browser's own timeout,
 * tens of seconds later, by which point any window has expired and every
 * reload qualifies as a fresh incident. That is an endless loop on exactly the
 * degraded network the recovery is meant to survive.
 */
const MAX_CHUNK_RELOADS = 1;

let storageWarned = false;

const readReloadCount = (): number => {
  try {
    return Number(window.sessionStorage.getItem(CHUNK_RELOAD_KEY)) || 0;
  } catch {
    // Storage is unavailable (Safari private mode, a sandboxed frame). Reading
    // that as "the budget is gone" is the safe direction: with nowhere to
    // record an attempt, nothing else would stop a reload loop. Say so once,
    // or "chunks never recover in a private window" looks like a ghost.
    if (!storageWarned) {
      storageWarned = true;
      console.error(
        '[staleChunk] sessionStorage is unavailable — chunk recovery is disabled for this tab',
      );
    }
    return MAX_CHUNK_RELOADS;
  }
};

/**
 * Closes the incident: a chunk arrived, so the budget is whole again.
 *
 * Without this the first dropout of a session would disable recovery for the
 * rest of the tab, and an hour later an unrelated dropout would land the user
 * on the error screen with no attempt made.
 */
export const noteChunkLoaded = (): void => {
  if (import.meta.env.SSR) {
    return;
  }
  try {
    if (window.sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
      window.sessionStorage.removeItem(CHUNK_RELOAD_KEY);
    }
  } catch {
    // Nothing was recorded either, so there is nothing to clear.
  }
};

/**
 * Reloads the page after a chunk failed to arrive over a healthy build.
 *
 * The other recovery above only fires when a deploy is behind the failure. What
 * is left is the common case in a mobile webview: the connection drops for the
 * moment a route chunk is requested. That single dropout is permanent damage,
 * because a module the browser failed to fetch is recorded as failed in the
 * module map — importing the same URL again returns the same error without
 * touching the network, so neither React nor a retry inside the loader can undo
 * it. Only a new document can, which is why the error screen's "try again"
 * button is a `location.reload()` and why doing it automatically is the fix:
 * the user is being asked to perform the recovery by hand today.
 *
 * Two things keep it from making matters worse. The budget above bounds it, and
 * it is spent before the reload starts, so a chunk that is genuinely gone
 * reaches the error screen on its second try. And the caller has to establish
 * that the network answers at all — `navigator.onLine` cannot: it reports
 * whether an interface exists, staying `true` through a dead radio, a captive
 * portal or broken DNS, which is precisely this failure. Reloading into that
 * replaces a page that still partly works with the browser's error page.
 *
 * @param networkIsUp whether something was just fetched successfully.
 * @returns whether a reload was started.
 */
export const recoverFromChunkFailure = (networkIsUp: boolean): boolean => {
  if (import.meta.env.SSR || !networkIsUp || navigator.onLine === false) {
    return false;
  }

  const spent = readReloadCount();
  if (spent >= MAX_CHUNK_RELOADS) {
    return false;
  }

  try {
    window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(spent + 1));
  } catch {
    return false;
  }

  window.location.reload();
  return true;
};

/** How long a reload is given before the caller is told it is not coming. */
const RELOAD_GRACE_MS = 10_000;

/**
 * Holds a pending render in place while a reload replaces the page.
 *
 * `window.location.reload()` has already been called, so in the normal case
 * nothing ever settles this — the document is replaced first. The rejection
 * exists for the case where the reload does not happen (a `beforeunload`
 * handler cancels it, a sandboxed frame forbids it): without it the route would
 * stay pending forever and the user would be left on a page that silently
 * stopped responding to navigation.
 */
export const pendingUntilReload = <T>(): Promise<T> =>
  new Promise((_resolve, reject) => {
    setTimeout(
      () =>
        reject(
          new Error(
            'a reload was started to recover a failed chunk but the page never went away — showing the route error instead',
          ),
        ),
      RELOAD_GRACE_MS,
    );
  });
